﻿/*
 * Purpose: Validate South African ID numbers and derive age for AuthForm.
 * Boundary: Custom hook only; pure validation logic; no UI.
 * Invariants:
 * - Accepts exactly 13 digits.
 * - Validates date of birth, gender, citizenship, and checksum.
 * - Computes age only when ID is valid.
 * - Example valid IDs (for age calculation reference):
 *   0903155000081 -> age 16
 *   0509265000083 -> age 20
 * - ASCII only.
 */

// Imports
import { useMemo } from 'react';

// Validator: SA ID format, date, and Luhn checksum
const validateSAID = (id) => {
    // Check length and digits
    if (id.length !== 13 || !/^\d{13}$/.test(id)) {
        return { valid: false };
    }

    // Extract components
    const yy = parseInt(id.substr(0, 2), 10);
    const mm = parseInt(id.substr(2, 2), 10);
    const dd = parseInt(id.substr(4, 2), 10);
    const gender = parseInt(id.substr(6, 4), 10);
    const citizenship = parseInt(id.substr(10, 1), 10);
    const checksum = parseInt(id.substr(12, 1), 10);

    // Derive birth year (1900/2000 series)
    const currentYY = new Date().getFullYear() % 100;
    const birthYear = yy > currentYY ? 1900 + yy : 2000 + yy;
    const birthDate = new Date(birthYear, mm - 1, dd);

    // Reject invalid date or ranges
    if (
        isNaN(birthDate.getTime()) ||
        mm < 1 ||
        mm > 12 ||
        dd < 1 ||
        dd > 31 ||
        gender < 0 ||
        gender > 9999 ||
        citizenship !== 0 && citizenship !== 1 ||
        birthDate.getFullYear() !== birthYear ||
        birthDate.getMonth() + 1 !== mm ||
        birthDate.getDate() !== dd
    ) {
        return { valid: false };
    }

    // Luhn checksum
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        let digit = parseInt(id[i], 10);
        if (i % 2 === 1) digit *= 2;
        if (digit > 9) digit -= 9;
        sum += digit;
    }
    if ((10 - (sum % 10)) % 10 !== checksum) {
        return { valid: false };
    }

    return { valid: true, birthDate };
};

// Hook useIDValidation:
// Validates South African ID and computes derived age
export function useIDValidation(idNumber) {
    // Derived: Validation result
    const validationResult = useMemo(() => validateSAID(idNumber), [idNumber]);

    // Derived: Age (only if valid)
    const age = useMemo(() => {
        if (!validationResult.valid || !validationResult.birthDate) return null;
        return Math.floor(
            (new Date() - validationResult.birthDate) / (365.25 * 24 * 60 * 60 * 1000)
        );
    }, [validationResult]);

    // Return: Validity and age
    return {
        isValidID: validationResult.valid,
        age,
    };
}